import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { lmsAdmin } from '../api'
import { relativeTime } from '../format'
import '../lms-admin.scss'
import { LmsIcon } from '../lms-icon'
import { LmsTrail, lmsTrail } from '../lms-trail'

// Build Log (docs/08 §8, design edxr-build-log-v1.0.html). Renders the
// structured build-log JSON (docs/06 §Build log) as a stepped timeline.
// v1 builds are synchronous and store steps only — the artifact's live
// xterm output, historical-archive, and error-details-modal states have
// no backing data and are deferred (deviations D-044).

const STEP_LABELS = {
  load_context: ['Load build context', 'Connection, preset, and course node resolved'],
  load_preset: ['Load preset', 'Platform preset config'],
  load_connection: ['Load connection', 'Connection config + overrides'],
  load_course_node: ['Load course node', 'Course metadata'],
  sign_credential: ['HMAC-sign credential', 'Signed identity payload for the launcher'],
  render_sco_html: ['Generate launcher index.html', 'Template substitution: course, deep links'],
  render_manifest: ['Generate imsmanifest.xml', 'SCORM 1.2 · single SCO'],
  create_zip: ['ZIP launcher package', 'imsmanifest.xml at root'],
  compute_sha256: ['Compute SHA-256', 'Package integrity hash'],
  upload_s3: ['Upload to storage', 'Object storage for admin download']
}

// What a build made: result.format, set by the service from the connection's
// delivery and its preset's scorm_version (the admin picks neither at build or
// download time, so the log is where the package says what it is). A log from
// before the field has no format and reads as it always did.
const FORMATS = {
  'scorm-1.2': { label: 'SCORM 1.2', manifest: 'imsmanifest.xml', unit: 'SCO' },
  'scorm-2004-4ed': { label: 'SCORM 2004 4th Edition', manifest: 'imsmanifest.xml', unit: 'SCO' },
  cmi5: { label: 'cmi5', manifest: 'cmi5.xml', unit: 'AU' }
}

const stepLabels = (name, format) => {
  if (format && name === 'render_manifest') return [`Generate ${format.manifest}`, `${format.label} · single ${format.unit}`]
  if (format && name === 'create_zip') return ['ZIP launcher package', `${format.manifest} at root`]
  return STEP_LABELS[name] || [name, null]
}

const fmtDuration = ms => {
  if (ms === undefined || ms === null) return '—'
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`
}

const fmtSize = bytes => {
  if (!bytes) return '—'
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`
}

export const ViewBuildLog = () => {
  const location = useLocation()
  const { packageId } = useParams()
  // The course the log was opened from (course-integration.js), so the trail can
  // name it; opened from an address alone, the log has no course to name.
  const from = location.state && location.state.course
  const trail = lmsTrail(
    { url: '/org/lms/courses', text: 'Courses' },
    ...(from && from.nodeId ? [{ url: `/org/lms/courses/${from.nodeId}/integration`, text: from.name || `Course ${from.nodeId}` }] : []),
    { url: `/org/lms/packages/${packageId}/build-log`, text: 'Build log' }
  )

  const [log, updateLog] = useState(null)
  const [error, updateError] = useState(null)

  useEffect(() => {
    lmsAdmin.getBuildLog(packageId).then(updateLog).catch(updateError)
  }, [packageId])

  const download = async () => {
    try {
      const url = await lmsAdmin.packageDownloadURL(packageId)
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      updateError(e)
    }
  }

  const format = (log && log.result && FORMATS[log.result.format]) || null
  const steps = (log && log.steps) || []
  const failedStep = steps.find(step => !step.ok)
  const succeeded = log && !failedStep
  const failedLabel = failedStep && (STEP_LABELS[failedStep.name] || [failedStep.name])[0]
  const started = log && relativeTime(log.started_at)

  return (
    <div className="lms-admin" data-testid="build-log">
      <LmsTrail trail={trail} />
      {error && (
        <div className="alert alert-danger" role="alert">
          {`Couldn't load the build log: ${error.message}`}
        </div>
      )}

      {log && (
        <div className="drawer-pane lms-page-card">
          <div className="build-log-head">
            <div className={`icon ${succeeded ? 'success' : 'danger'}`}>
              <LmsIcon name="list-check" />
            </div>
            <div className="body">
              <h2>
                Build log
                <span className={`head-pill ${succeeded ? 'success' : 'failed'}`} data-testid="build-status">
                  {succeeded ? '✓ Succeeded' : '✗ Failed'}
                </span>
              </h2>
              <p className="target">
                Package <code>#{log.package_id}</code>
                {started ? ` · built ${started.text}` : ''}
              </p>
            </div>
          </div>

          <div className="drawer-body">
            {succeeded ? (
              <div className="build-banner success" data-testid="build-banner">
                <LmsIcon name="circle-check" />
                <div>
                  <strong>Build succeeded in {fmtDuration(log.duration_ms)}.</strong> The launcher package is ready to
                  download and import into your LMS. It's a thin {format ? format.label : 'SCORM'} dispatch shell — when launched by an LMS user,
                  it opens the course in EducationXR.
                </div>
              </div>
            ) : (
              <div className="build-banner danger" data-testid="build-banner">
                <LmsIcon name="circle-exclamation" />
                <div>
                  <strong>Build failed at "{failedLabel}".</strong> Check this connection's package key (Connections →
                  this connection → Package key). Rotate it if no active key exists, then rebuild.
                </div>
              </div>
            )}

            <div className="summary-row">
              <div className="summary-stat">
                <div className="stat-label">Duration</div>
                <div className="stat-value mono">{fmtDuration(log.duration_ms)}</div>
                <div className="stat-sub">typical: 1-3s</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Size</div>
                <div className="stat-value">{fmtSize(log.result && log.result.size_bytes)}</div>
                <div className="stat-sub">{format ? format.label : 'SCORM'} dispatch launcher</div>
              </div>
              {format && (
                <div className="summary-stat" data-testid="build-format">
                  <div className="stat-label">Format</div>
                  <div className="stat-value">{format.label}</div>
                  <div className="stat-sub">set by the connection's preset</div>
                </div>
              )}
              <div className="summary-stat">
                <div className="stat-label">Package ID</div>
                <div className="stat-value mono">#{log.package_id}</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">SHA-256</div>
                <div className="stat-value mono">{((log.result && log.result.sha256) || '—').slice(0, 12)}</div>
                <div className="stat-sub">package integrity</div>
              </div>
            </div>

            <div className="section-head">
              <h3>Pipeline</h3>
              <span className="helper">{steps.length} steps</span>
            </div>
            <div className="pipeline">
              {steps.map(step => {
                const [label, sub] = stepLabels(step.name, format)
                return (
                  <div className={`pipeline-step ${step.ok ? 'done' : 'failed'}`} key={step.name} data-testid={`step-${step.name}`}>
                    <div className="step-icon">
                      <LmsIcon name={step.ok ? 'check' : 'xmark'} />
                    </div>
                    <div className="step-name">
                      {label}
                      {sub && <div className="step-sub">{sub}</div>}
                    </div>
                    <div className="step-duration">{fmtDuration(step.duration_ms)}</div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="drawer-foot">
            <span className="left">
              {succeeded
                ? 'A launcher shows Update available when the course has changed since it was built. Regenerate it from the course page to bring it up to date.'
                : 'Builds are synchronous; a retry runs the full pipeline again.'}
            </span>
            <span className="spacer" />
            {succeeded && (
              <button className="btn btn-light border btn-sm" type="button" data-testid="download-package" onClick={download}>
                <LmsIcon name="download" className="me-1" />
                Download package
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
